import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { useConstructorDefaultParameters } from 'common/api/constructor/useConstructorDefaultParameters';
import {
    ConstructorReturnObject,
    EarnTypeConstructorFilter,
    constructorToBrand,
    processOnBonusBrands,
} from 'common/api/constructor/util';
import { constructorClient } from 'third-party-integrations/constructor';
import { BaseLandingPage } from 'features/landing-pages/base/BaseLandingPage';
import { SearchGrid } from 'components/search-grid/SearchGrid';
import { TopPicksCard } from 'features/search/components/TopPicksCard';
import { Brand } from 'common/features/store/duck/search-generic/utils/searchResultBrandsToBrands';
import { useSearchGridStyles } from 'features/search/components/hooks';
import * as Progress from '@radix-ui/react-progress';

enum EarnMoreTypeMapping {
    flights = 'Flight',
    cruises = 'Cruise',
}

interface HeaderProps {
    title: string;
}

interface ViewingResultsProps {
    to: number;
    total: number;
}

const Header = ({ title }: HeaderProps) => {
    return (
        <div className="flex justify-center items-center bg-pale-blue text-[28px] md:text-[42px] p-6 md:p-12 text-brand-dark md:mb-2 flex-col">
            <p className="font-[400] capitalize">{title}</p>
        </div>
    );
};

const ViewingResults = ({ to, total }: ViewingResultsProps) => {
    if (!total || !to) {
        return null;
    }

    const progressPercentage = (to / total) * 100;

    return (
        <div className="mb-8 mt-4 text-center">
            <div className="flex flex-col items-center mb-4">
                <p className="text-grey-1 text-[14px] leading-[22px]">
                    Viewing <span className="font-bold text-brand">{to}</span> of{' '}
                    <span className="font-bold text-brand">{total}</span> Results
                </p>
                <Progress.Root
                    className="rounded-3xl bg-grey-5 h-2"
                    style={{ width: 180 }}
                    value={to}
                    max={total}
                >
                    <Progress.Indicator
                        className="rounded-3xl bg-bonus-pink h-2 transition-all duration-300"
                        style={{ width: `${progressPercentage}%` }}
                    />
                </Progress.Root>
            </div>
        </div>
    );
};

const ErrorState = () => (
    <div className="flex justify-center items-center py-12">
        <div className="text-center">
            <p className="text-red-500 mb-4">
                Failed to load brands for this category. Please try again.
            </p>
        </div>
    </div>
);

export const EarnMoreByTypePage = () => {
    const { typeId } = useParams<{ typeId: string }>();

    const mappedTypeId = useMemo(() => {
        if (typeId && typeId in EarnMoreTypeMapping) {
            return EarnMoreTypeMapping[typeId as keyof typeof EarnMoreTypeMapping];
        }

        return null;
    }, [typeId]);

    const baseFilters = useConstructorDefaultParameters({
        resultsPerPage: 200,
        filters: {
            EarnType: [EarnTypeConstructorFilter.GiftCards],
        },
    });

    const [searchGridStyles] = useSearchGridStyles<'325', '280'>('325', '280');

    const [pageBrands, setPageBrands] = useState<Brand[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const totalResultsRef = useRef<number>(0);

    const fetchBrands = useCallback(async () => {
        if (!mappedTypeId) {
            return;
        }

        setIsLoading(true);

        try {
            const constructorResponse = await constructorClient.browse.getBrowseResults(
                'group_id',
                'customerCategories-travel',
                {
                    ...baseFilters.current,
                    filters: {
                        ...baseFilters.current.filters,
                        group_id: `${mappedTypeId} Gift Cards`,
                    },
                    page: 1,
                    fmtOptions: {
                        groups_max_depth: 1,
                    },
                }
            );

            const constructorBrands = constructorResponse.response?.results || [];
            const validBrands = processOnBonusBrands(
                constructorBrands as ConstructorReturnObject[],
                false
            );
            const brands = constructorToBrand(validBrands);

            setPageBrands(brands);
            totalResultsRef.current = constructorResponse.response?.total_num_results ?? 0;
        } catch (err) {
            console.error('Failed to fetch brands:', err);
            setPageBrands([]);
            totalResultsRef.current = 0;
        } finally {
            setIsLoading(false);
        }
    }, [baseFilters, mappedTypeId]);

    useEffect(() => {
        fetchBrands();
    }, [fetchBrands]);

    const renderers = useMemo(
        () => ({
            header: () => <Header title={`${mappedTypeId} Gift Cards`} />,
            footer: () => <ViewingResults to={pageBrands.length} total={totalResultsRef.current} />,
        }),
        [mappedTypeId, pageBrands.length]
    );

    return (
        <BaseLandingPage firebaseConfigKey="featured_page_hero_id" renderers={renderers}>
            <div className="mt-10">
                {!mappedTypeId ? (
                    <ErrorState />
                ) : (
                    <SearchGrid
                        categoryName={mappedTypeId}
                        ItemComponent={TopPicksCard}
                        items={pageBrands}
                        isLoading={isLoading}
                        containerStyles={searchGridStyles}
                    />
                )}
            </div>
        </BaseLandingPage>
    );
};
